import type { ElementType } from "react"

import { filledColumns } from "@/components/marketplace/discovery/filled-columns"
import type { Category } from "@/components/marketplace/types"
import { Grid, Text } from "@/components/ui"
import { TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"

const TABLET_COLS = { 2: "@2xl:grid-cols-2", 3: "@2xl:grid-cols-3", 4: "@2xl:grid-cols-4", 5: "@2xl:grid-cols-5" } as const

type Columns = keyof typeof TABLET_COLS

interface CategoryGridProps {
  items: Category[]
  framed?: boolean
  limits?: { narrow: number; wide: number }
}

const tileDisplay = (index: number, limits?: { narrow: number; wide: number }) => {
  if (!limits) return "flex"
  const narrow = index < limits.narrow
  const wide = index < limits.wide
  if (narrow === wide) return "flex"
  return narrow ? "flex @2xl:hidden" : "hidden @2xl:flex"
}

export const CategoryGrid = ({ items, framed = false, limits }: CategoryGridProps) => {
  const columns: Columns = limits ? (Math.min(Math.max(limits.wide, 2), 4) as Columns) : filledColumns(items.length)
  return (
    <div className="@container">
      <Grid cols={2} colsDesktop={columns} gap="250" className={`px-gut ${framed ? "gap-y-375" : "gap-y-350"} @2xl:gap-x-500 @2xl:gap-y-750 ${TABLET_COLS[columns]}`}>
        {items.map((item, index) => {
          const Tile: ElementType = item.href ? Link : "div"
          return (
            <Tile
              key={item.id}
              {...(item.href ? { href: item.href } : {})}
              data-nelo-serve={item.serveId}
              className={`press focus-visible:ring-palette-bar group flex-col focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${tileDisplay(index, limits)}`}
            >
              <div
                className={`aspect-category-tile-sm bg-surface-muted @2xl:aspect-category-tile relative overflow-hidden ${framed ? "rounded-10x @2xl:shadow-tile @2xl:rounded-12x" : "rounded-8x"}`}
              >
                <TileImage
                  src={item.image}
                  crop={item.imageCrop}
                  alt={item.imageAlt ?? ""}
                  fill
                  sizes="(max-width: 768px) 48vw, 320px"
                  className="duration-moderate ease-productive object-cover object-[76%_50%] transition-transform motion-safe:group-hover:scale-105"
                />
              </div>
              <Text
                as="div"
                variant="body"
                family="text"
                tone="default"
                truncate
                className={framed ? "text-copy @2xl:text-copy-lg pt-125 @2xl:pt-250" : "@2xl:text-body-lg pt-125 @2xl:pt-250"}
              >
                {item.label}
              </Text>
            </Tile>
          )
        })}
      </Grid>
    </div>
  )
}
